import * as Yup from "yup"

import type { AppLocalization } from "../../../../../modules/localization/appLocalization"
import { CreateUpdateBookingResourceTableSetups } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import {
    DynamicConfigKey,
    BookingConfig,
    BookingCustomFieldsGroup,
    ExtendedDynamicConfigKey,
    ValidationType,
} from "./formTypes"
import { SelectedMainUserState } from "../../createOrderTypes"
import { buildFieldSchema } from "./helpers"
import { applyFieldFallback } from "./applyFieldFallback"
import { getAllConfigFields, getConfigLockedKeys } from "./formLib"
import { InitialValuesState } from "../resource/ResourceBookingDraftContext"
import { WORKGROUP_FIELD } from "../drawer/meetingDetails/WorkgroupSelect"

const initialValuesMapping: Partial<Record<ExtendedDynamicConfigKey, (init: InitialValuesState) => any>> = {
    [DynamicConfigKey.MEETING_NAME]: (init) => init.meetingName,
    [DynamicConfigKey.PRIVATE_MEETING]: (init) => init.isPrivate,
    [DynamicConfigKey.EXTERNAL_MEETING]: (init) => init.isExternal,
    [DynamicConfigKey.MEETING_RESPONSIBLE]: ({ organizers }) => organizers ?? undefined,
    [DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE]: ({ attendees }) => (attendees?.length ? true : undefined),
    [DynamicConfigKey.ADD_PARTICIPANTS_FORM]: ({ attendees }) => (attendees?.length ? attendees : undefined),
    [DynamicConfigKey.SERVICE_CENTER_MESSAGE]: (init) => init?.messageToServiceCenter || "",
}

export function generateBookingInitialValues(
    config: BookingConfig,
    customFields: BookingCustomFieldsGroup,
    initialValues: InitialValuesState | null,
    user: SelectedMainUserState,
): Record<ExtendedDynamicConfigKey | string, any> {
    const allFields = getAllConfigFields(config).filter(({ active }) => active)
    const lockedKeys = getConfigLockedKeys(config)

    const initConfigValues = allFields.reduce((acc, { key, inputField, selectValues }) => {
        if (key === DynamicConfigKey.MEETING_RESPONSIBLE) {
            acc[key] = {
                name: user.displayName,
                id: user.userId,
            }
            return acc
        }

        if (inputField) {
            applyFieldFallback(acc, inputField, key, selectValues)
        }
        return acc
    }, {} as Record<ExtendedDynamicConfigKey, any>)

    const mergedValues = { ...initConfigValues }

    if (allFields.some((f) => f.key === DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE)) {
        // Config may seed the toggle via predefined.value as a string/number — normalize to a real
        // boolean here so the strict `=== true` checks downstream stay reliable.
        const rawToggle = mergedValues[DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE]
        mergedValues[DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE] =
            rawToggle === true || rawToggle === "true" || rawToggle === 1
    }

    if (allFields.some((f) => f.key === DynamicConfigKey.ADD_PARTICIPANTS_FORM)) {
        mergedValues[DynamicConfigKey.ADD_PARTICIPANTS_FORM] ??= []
    }

    if (initialValues) {
        ;(Object.keys(mergedValues) as DynamicConfigKey[]).forEach((key) => {
            // A hidden field with a predefined value is not editable, so the config wins over
            // whatever the outer form or the saved order carries for that key.
            if (lockedKeys.has(key)) return

            const getter = initialValuesMapping[key]
            if (!getter) return

            const value = getter(initialValues)

            // Skip updating mergedValues if:
            // 1. The value is undefined (no update necessary)
            // 2. The current merged value and the new value have the same id
            //    (to prevent unnecessary overwriting of equivalent objects and
            //    to avoid causing extra re-renders or infinite loops in Formik)
            // This is important because Formik tracks object references strictly,
            // and even minor unnecessary changes can trigger full form resets or reinitializations.

            if (value === undefined || (value?.id && mergedValues[key]?.id === value?.id)) {
                return
            }

            mergedValues[key] = value
        })
    }

    const hasTableArrangements = allFields.some(({ key }) => key === DynamicConfigKey.TABLE_ARANGEMENTS)

    const processedResources: CreateUpdateBookingResourceTableSetups[] = initialValues?.resources
        ? initialValues.resources.map(({ id, tableSetups = [] }) => ({
              id,
              tableSetups: hasTableArrangements ? [] : tableSetups,
          }))
        : []

    // Not a config field, so it is carried explicitly instead of through the config-driven merge.
    const workgroupId = initialValues?.workgroupId ?? null

    return { ...mergedValues, ...customFields, resources: processedResources, [WORKGROUP_FIELD]: workgroupId }
}

export function generateBookingValidationSchema(
    config: BookingConfig,
    l10n: AppLocalization,
): Yup.ObjectSchema<Record<ExtendedDynamicConfigKey, any>> {
    const shape: Partial<Record<ExtendedDynamicConfigKey, Yup.Schema<any>>> = {}

    const allFields = getAllConfigFields(config)

    const activeFieldKeys = new Set(allFields.filter(({ active }) => active).map(({ key }) => key))

    allFields.forEach((config) => {
        const field = config.inputField
        const key = config.key
        const label = config.label || config.key

        if (!activeFieldKeys.has(key)) return
        if (field?.hidden) return

        shape[key] = buildFieldSchema(field, label, l10n)
    })

    // ADD_PARTICIPANTS_FORM is stored as an array and only collected when the toggle is on.
    // Yup.required() does not fail for empty arrays, so enforce non-empty explicitly, gated on the toggle.
    const participantsFormField = allFields.find((f) => f.key === DynamicConfigKey.ADD_PARTICIPANTS_FORM)
    if (participantsFormField && activeFieldKeys.has(DynamicConfigKey.ADD_PARTICIPANTS_FORM)) {
        const label = participantsFormField.label || participantsFormField.key
        const isRequired =
            Boolean(participantsFormField.inputField?.isRequired) ||
            Boolean(participantsFormField.inputField?.validations?.some((v) => v.type === ValidationType.REQUIRED))
        const msg = l10n.getString("create-order-validation-required", { label })

        shape[DynamicConfigKey.ADD_PARTICIPANTS_FORM] = isRequired
            ? Yup.array().when(DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE, {
                  is: true,
                  then: (schema) => schema.min(1, msg).required(msg),
                  otherwise: (schema) => schema.notRequired(),
              })
            : Yup.array().notRequired()
    }

    return Yup.object().shape(shape as Record<DynamicConfigKey, Yup.Schema<any>>)
}
